import React, { useEffect, useState } from "react";
function getByPath(obj, path) {
  if (obj == null) return undefined;
  if (Array.isArray(path)) path = path.join(".");
  return String(path)
    .split(".")
    .reduce((acc, p) => (acc == null ? undefined : acc[p]), obj);
}

function buildLabel(item, displayKey) {
  if (!item) return "";
  if (!displayKey) return String(item);
  if (Array.isArray(displayKey)) {
    return displayKey.map(k => {
      const v = getByPath(item, k);
      return v == null ? "" : String(v);
    }).filter(Boolean).join(" ");
  }
  const v = getByPath(item, displayKey);
  return v == null ? "" : String(v);
}

function normalizeResponse(res) {
  if (!res) return { data: [] };
  if (Array.isArray(res)) return { data: res };
  if (Array.isArray(res.data)) return { data: res.data };
  if (res.data && Array.isArray(res.data.data)) return { data: res.data.data };
  // fallback: try to find first array
  for (const k in res) {
    if (Array.isArray(res[k])) return { data: res[k] };
  }
  return { data: [] };
}

export default function SelectDropDown({
  api,
  apiParams = {},
  displayKey = "name",
  valueKey = "_id",
  value,
  onChange,
  placeholder = "All",
  className = "form-select",
  sort = true,
  disabled = false,
  ...rest
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (typeof api !== "function") {
      setError(new Error("api prop must be a function"));
      return;
    }
    setLoading(true);
    setError(null);
    // call api with provided params
    Promise.resolve(api(apiParams))
      .then(res => {
        if (cancelled) return;
        const normalized = normalizeResponse(res);
        const data = Array.isArray(normalized.data) ? normalized.data : [];
        setOptions(data);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("SelectDropDown api error:", err);
        setError(err);
        setOptions([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [api, JSON.stringify(apiParams)]); // re-fetch if api or params change

  // sort by label if requested
  const renderedOptions = (options || []).slice();
  if (sort) {
    renderedOptions.sort((a, b) => {
      const A = buildLabel(a, displayKey).toLowerCase();
      const B = buildLabel(b, displayKey).toLowerCase();
      if (A < B) return -1;
      if (A > B) return 1;
      return 0;
    });
  }

  return (
    <>
      <select
        className={className}
        value={value || ""}
        // onChange={(e) => onChange && onChange(e.target.value, e.target.options[e.target.selectedIndex].dataset.keyLabel)}
        onChange={(e) => {
          const opt = e.target.options[e.target.selectedIndex];
          const keyLabel = {
            key: opt.dataset.key,
            label: opt.dataset.label
          };
          onChange && onChange(e.target.value, keyLabel);
        }}
        disabled={disabled || loading}
        {...rest}
      >
        <option value="">{placeholder}</option>
        {renderedOptions.map((opt, idx) => {
          const key = getByPath(opt, valueKey) ?? idx;
          const label = buildLabel(opt, displayKey);
          return (
            <option key={String(key) + idx} data-key={key} data-label={label} value={getByPath(opt, valueKey) ?? ""}>
              {label}
            </option>
          );
        })}
      </select>
      {loading && <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>Loading...</div>}
      {error && <div style={{ fontSize: 12, color: "crimson", marginTop: 6 }}>Error loading options</div>}
    </>
  );
}
